import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { assets, onAssetError } from "./assets";
import { CONTACT } from "./data";
import { ArrowUpRight, CloseIcon, MenuIcon } from "./icons";
import { useLanding } from "./LandingContext";

const navActiveStyle = {
  padding: "10px 22px",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 600,
  color: "#1a1416",
  background: "#fff",
  boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
};

const navInactiveStyle = {
  padding: "10px 20px",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 500,
  color: "rgba(255,255,255,0.92)",
};

const sideLink = {
  padding: "13px 14px",
  borderRadius: 14,
  fontSize: 15,
  fontWeight: 600,
  color: "#1a1416",
  display: "block",
  textDecoration: "none",
};

function MobileMenu({ closeMenu, goTo }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  const menu = (
    <div className="chz-mobile-root" role="presentation">
      <button
        type="button"
        className="chz-mobile-backdrop"
        aria-label="Close menu"
        onClick={closeMenu}
      />
      <nav className="chz-mobile-sidebar" aria-label="Mobile menu">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <img src={assets.logo} alt="Chainz" onError={onAssetError} style={{ width: 30, height: "auto", filter: "brightness(0)" }} />
          <button
            type="button"
            onClick={closeMenu}
            aria-label="Close menu"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              background: "rgba(26,20,22,0.05)",
              border: "none",
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <a href="#services" onClick={goTo("services", "fulfillment", "home")} style={sideLink}>
          Services
        </a>
        <a href="#products" onClick={goTo("products", "products", "home")} style={sideLink}>
          Solutions
        </a>
        <a href="#how" onClick={goTo("how", "fulfillment", "home")} style={sideLink}>
          How it works
        </a>
        <a href="/about" onClick={goTo("about", null, "about")} style={sideLink}>
          About
        </a>
        <a href="/book?intent=partnership" style={sideLink} onClick={closeMenu}>
          Become a Partner
        </a>
        <a href="/book?intent=demo" style={sideLink} onClick={closeMenu}>
          Book a Demo
        </a>
        <a href="/contact" onClick={goTo("contact-page", null, "contact")} style={sideLink}>
          Contact
        </a>
        <div style={{ height: 1, background: "rgba(26,20,22,0.08)", margin: "10px 0" }} />
        <a href={CONTACT.track} target="_blank" rel="noopener noreferrer" style={sideLink} onClick={closeMenu}>
          Track Order
        </a>
        <a href={CONTACT.login} target="_blank" rel="noopener noreferrer" style={sideLink} onClick={closeMenu}>
          Login
        </a>
      </nav>
    </div>
  );

  return createPortal(menu, document.body);
}

export default function LandingNav() {
  const location = useLocation();
  const { navSolid, menuOpen, toggleMenu, closeMenu, goTo } = useLanding();

  const isHome = location.pathname === "/" || location.pathname === "";
  const isAbout = location.pathname.startsWith("/about");
  const isContact = location.pathname.startsWith("/contact");

  return (
    <>
      <div
        className={`nav-wrap${navSolid ? " nav-solid" : ""}`}
        style={{ position: "sticky", top: 0, zIndex: 100, padding: "14px 16px" }}
        data-screen-label="Nav"
      >
        <div className="nav-inner">
          <div className="nav-brand">
            <Link to="/" onClick={goTo("hero", null, "home")} aria-label="Chainz home" style={{ position: "relative", zIndex: 2 }}>
              <img src={assets.logo} alt="Chainz" onError={onAssetError} style={{ width: 34, height: "auto", display: "block" }} />
            </Link>
          </div>

          <div className="navpill">
            <a
              href="#hero"
              className={isHome ? undefined : "navlite"}
              onClick={goTo("hero", null, "home")}
              style={isHome ? navActiveStyle : navInactiveStyle}
            >
              Home
            </a>
            <a className="navlite" href="#services" onClick={goTo("services", "fulfillment", "home")} style={navInactiveStyle}>
              Services
            </a>
            <a className="navlite" href="#products" onClick={goTo("products", "products", "home")} style={navInactiveStyle}>
              Solutions
            </a>
            <a className="navlite" href="#how" onClick={goTo("how", "fulfillment", "home")} style={navInactiveStyle}>
              How it works
            </a>
            <a
              className={isAbout ? undefined : "navlite"}
              href="/about"
              onClick={goTo("about", null, "about")}
              style={isAbout ? navActiveStyle : navInactiveStyle}
            >
              About
            </a>
            <a
              className={isContact ? undefined : "navlite"}
              href="/contact"
              onClick={goTo("contact-page", null, "contact")}
              style={isContact ? navActiveStyle : navInactiveStyle}
            >
              Contact
            </a>
          </div>

          <div className="navbtns">
            <button
              type="button"
              className="navburger"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleMenu();
              }}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              <MenuIcon />
            </button>
            <a
              className="navutil"
              href={CONTACT.track}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "10px 18px",
                borderRadius: 999,
                fontSize: 13.5,
                fontWeight: 600,
                color: "#fff",
                background: "linear-gradient(150deg, rgba(255,255,255,0.24), rgba(255,255,255,0.08))",
                border: "1px solid rgba(255,255,255,0.4)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 20px rgba(0,0,0,0.18)",
              }}
            >
              Track Order
            </a>
            <a
              className="navutil"
              href={CONTACT.login}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "10px 18px",
                borderRadius: 999,
                fontSize: 13.5,
                fontWeight: 600,
                color: "#fff",
                background: "linear-gradient(150deg, rgba(255,255,255,0.24), rgba(255,255,255,0.08))",
                border: "1px solid rgba(255,255,255,0.4)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 20px rgba(0,0,0,0.18)",
              }}
            >
              Login
            </a>
          </div>
        </div>
      </div>

      {menuOpen ? <MobileMenu closeMenu={closeMenu} goTo={goTo} /> : null}

      <Link className="navcta-float" to="/book?intent=partnership">
        Become a Partner
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "#fff",
            display: "grid",
            placeItems: "center",
            color: "#1a1416",
          }}
        >
          <ArrowUpRight />
        </span>
      </Link>
    </>
  );
}
